// ProgramReceiptV1 — the ONE program-wide receipt envelope (#4681 E3).
//
// E1 established that no canonical receipt envelope existed on main; the
// rehearsal harness (and later E4 drift orchestration and E5 rating records)
// would each have invented their own. This is the single source (Law 5): one
// closed kind set covering the cold-start certification stage gates plus the
// reusable program kinds, one stamping constructor, one runtime validator
// with named violations — the E2 idiom, frozen from day one: a missing
// marker is a named violation, never tolerated.
//
// The K7 law is built in mechanically: a receipt is mintable only with
// evidence refs at the SAME capture identity (every evidenceRef.captureId
// must equal the receipt's captureId). A heuristic/judge PASS may ride the
// payload as evidence, but it can never mint a receipt — minting requires
// evidence refs, and this envelope has no heuristic-verdict kind.
//
// This module is CLIENT-REACHABLE and must stay that way: it is published
// through the public `@helix/lib` barrel, which the Next.js Edge middleware
// graph reaches, so it may never depend on a `node:` builtin (the boundary
// law in packages/lib/scripts/client-reachable-boundary.spec.mjs). The one
// half that needs Node crypto — evidence verification and its content
// digest — lives in ./program-receipt-evidence-digest and is published only
// through `@helix/lib/server`. Same verifier, same digest, one receipt
// semantics; only the entry point differs.

import {
  isCanonicalIsoTimestamp,
  isNonEmptyString,
  isPlainJsonValue,
  isRecord,
} from './program-receipt-shape-predicates';

export const PROGRAM_RECEIPT_SCHEMA_VERSION = 'program_receipt_v1' as const;

// Closed kind set. Stage gates follow the cold-start certification loop
// (#4681): entry as a ProjectedQuestionV1, explicit capability gates,
// sanctioned ingestion, answer capture, rating, improvement request, drift
// drill, replay. assembly_disposition (E3 stage-7 reuse/extend/build call)
// and drift_observation (E4's standing monitor) are declared now so E4/E5
// reuse THIS envelope instead of minting their own.
export const PROGRAM_RECEIPT_KINDS = [
  'question_entry',
  'capability_gate',
  'data_ingest',
  'answer_capture',
  'rating',
  'improvement_request',
  'drift_drill',
  'replay_verification',
  'assembly_disposition',
  'drift_observation',
] as const;

export type ProgramReceiptKind = (typeof PROGRAM_RECEIPT_KINDS)[number];

export const PROGRAM_RECEIPT_PRODUCER_CLASSES = [
  'harness',
  'module',
  'service',
  'actor',
] as const;

export type ProgramReceiptProducerClass =
  (typeof PROGRAM_RECEIPT_PRODUCER_CLASSES)[number];

export const PROGRAM_RECEIPT_EVIDENCE_KINDS = [
  'validated_capture',
  'artifact',
  'ledger_record',
] as const;

export type ProgramReceiptEvidenceKind = (typeof PROGRAM_RECEIPT_EVIDENCE_KINDS)[number];

// The stage gates of the cold-start certification loop — a SUBSET of the
// kind vocabulary, declared here so consumers (the rehearsal harness) derive
// it canonically instead of restating it. assembly_disposition and
// drift_observation are program-reuse kinds with no rehearsal stage gate.
export const PROGRAM_RECEIPT_STAGE_GATES = [
  'question_entry',
  'capability_gate',
  'data_ingest',
  'answer_capture',
  'rating',
  'improvement_request',
  'drift_drill',
  'replay_verification',
] as const;

export type ProgramReceiptEvidenceRef = {
  evidenceKind: ProgramReceiptEvidenceKind;
  refId: string;
  captureId: string;
  /** Who established this reference (the canonical verifier's identity). */
  verifierId: string;
  /** When it was established (canonical ISO). */
  verifiedAt: string;
  /** sha256 over the stable serialization of the verified content — binds
   * the ref to the exact artifact verified; post-mint mutation is detectable. */
  contentDigest: string;
};

// The evidence ref is produced by the canonical verifier,
// verifyReceiptEvidence — exported from `@helix/lib/server` because its
// contentDigest is a Node SHA-256. Callers cannot hand-author a ref by
// convention alone: the verifier requires the evidence CONTENT as an already
// parsed record, i.e. the producer must have persisted and read back the
// artifact it references. The validator below still enforces the ref's
// stamps client-side, so a forged ref is rejected wherever it is checked.

export type ProgramReceiptV1 = {
  schemaVersion: typeof PROGRAM_RECEIPT_SCHEMA_VERSION;
  receiptId: string;
  kind: ProgramReceiptKind;
  runId: string;
  captureId: string;
  subjectRefs: {
    questionRef?: string;
    moduleId?: string;
    intentId?: string;
  };
  producer: {
    producerClass: ProgramReceiptProducerClass;
    producerId: string;
  };
  createdAt: string;
  evidenceRefs: readonly ProgramReceiptEvidenceRef[];
  payload: Record<string, unknown>;
};

export type MintProgramReceiptInput = Omit<ProgramReceiptV1, 'schemaVersion'>;

// The canonical runtime validator. Named violations; an empty list admits.
// The marker is REQUIRED (missing -> version_missing, wrong ->
// version_mismatch): a frozen boundary that admits an unstamped object is not
// frozen (the E2-AMEND lesson, applied at declaration time).
export function validateProgramReceipt(value: unknown): readonly string[] {
  const violations: string[] = [];
  if (!isRecord(value)) return ['program_receipt_not_an_object'];
  if (!('schemaVersion' in value) || value.schemaVersion === undefined) {
    violations.push('program_receipt_version_missing');
  } else if (value.schemaVersion !== PROGRAM_RECEIPT_SCHEMA_VERSION) {
    violations.push('program_receipt_version_mismatch');
  }
  if (!isNonEmptyString(value.receiptId)) violations.push('program_receipt_missing_receipt_id');
  if (
    !isNonEmptyString(value.kind) ||
    !PROGRAM_RECEIPT_KINDS.includes(value.kind as ProgramReceiptKind)
  ) {
    violations.push('program_receipt_kind_unknown');
  }
  if (!isNonEmptyString(value.runId)) violations.push('program_receipt_missing_run_id');
  if (!isNonEmptyString(value.captureId)) violations.push('program_receipt_missing_capture_id');
  if (!isRecord(value.subjectRefs)) {
    violations.push('program_receipt_subject_refs_invalid');
  } else {
    // Every PRESENT subject field must be a non-empty string — a present
    // null or a wrong-typed value fails the published optional contract.
    for (const field of ['questionRef', 'moduleId', 'intentId'] as const) {
      const fieldValue = value.subjectRefs[field];
      if (fieldValue !== undefined && !isNonEmptyString(fieldValue)) {
        violations.push(`program_receipt_subject_ref_invalid_${field}`);
      }
    }
    if (
      !isNonEmptyString(value.subjectRefs.questionRef) &&
      !isNonEmptyString(value.subjectRefs.moduleId) &&
      !isNonEmptyString(value.subjectRefs.intentId)
    ) {
      violations.push('program_receipt_subject_refs_empty');
    }
  }
  if (!isRecord(value.producer)) {
    violations.push('program_receipt_producer_invalid');
  } else {
    // Type-check before comparing: String(['harness']) === 'harness' would
    // admit an array through a coercion.
    if (
      typeof value.producer.producerClass !== 'string' ||
      !PROGRAM_RECEIPT_PRODUCER_CLASSES.includes(
        value.producer.producerClass as ProgramReceiptProducerClass,
      )
    ) {
      violations.push('program_receipt_producer_class_unknown');
    }
    if (!isNonEmptyString(value.producer.producerId)) {
      violations.push('program_receipt_producer_id_missing');
    }
  }
  if (!isCanonicalIsoTimestamp(value.createdAt)) {
    violations.push('program_receipt_created_at_invalid');
  }
  if (!Array.isArray(value.evidenceRefs) || value.evidenceRefs.length === 0) {
    // The K7 law: no evidence, no receipt. A heuristic PASS riding a payload
    // can never satisfy this — it is not an evidence ref.
    violations.push('program_receipt_evidence_refs_empty');
  } else {
    for (const ref of value.evidenceRefs) {
      if (!isRecord(ref) || !isNonEmptyString(ref.refId)) {
        violations.push('program_receipt_evidence_ref_invalid');
        continue;
      }
      if (
        typeof ref.evidenceKind !== 'string' ||
        !PROGRAM_RECEIPT_EVIDENCE_KINDS.includes(
          ref.evidenceKind as ProgramReceiptEvidenceKind,
        )
      ) {
        violations.push('program_receipt_evidence_kind_unknown');
      }
      if (ref.captureId !== value.captureId) {
        violations.push('program_receipt_evidence_capture_mismatch');
      }
      // Authority: the ref must carry the canonical verifier's typed stamps.
      if (!isNonEmptyString(ref.verifierId)) {
        violations.push('program_receipt_evidence_unverified');
      }
      if (!isNonEmptyString(ref.contentDigest) || !ref.contentDigest.startsWith('sha256:')) {
        violations.push('program_receipt_evidence_unverified');
      }
      if (!isCanonicalIsoTimestamp(ref.verifiedAt)) {
        violations.push(
          isNonEmptyString(ref.verifiedAt)
            ? 'program_receipt_evidence_verified_at_invalid'
            : 'program_receipt_evidence_unverified',
        );
      }
    }
  }
  // The payload promises an immutable serializable snapshot: only plain
  // JSON-domain values (Map/Date/class instances keep mutable internal state
  // under Object.freeze and break persistence round-trips).
  if (!isRecord(value.payload)) {
    violations.push('program_receipt_payload_invalid');
  } else if (!isPlainJsonValue(value.payload)) {
    violations.push('program_receipt_payload_not_serializable');
  }
  return violations;
}

// The ONE mint path: validates the full shape and stamps the marker. Throws
// on any violation — receipt producers are in-repo, so a throw here is a
// wiring defect, never a user-facing path. The minted receipt is a deep-cloned,
// frozen graph: a caller that mutates its input after minting cannot rewrite
// an already-admitted receipt (persisted proof must equal validated proof).
export function mintProgramReceipt(input: MintProgramReceiptInput): ProgramReceiptV1 {
  // Validate the INPUT first so a non-cloneable payload (functions, symbols)
  // fails with the named violation instead of a raw DataCloneError, then
  // validate the CLONE — the snapshot actually returned — so inherited
  // getters/prototype fields that vanish at clone time cannot smuggle a
  // receipt that fails its own validator.
  const stamped: ProgramReceiptV1 = { ...input, schemaVersion: PROGRAM_RECEIPT_SCHEMA_VERSION };
  const inputViolations = validateProgramReceipt(stamped);
  if (inputViolations.length > 0) {
    throw new Error(`program receipt mint rejected: ${inputViolations.join(', ')}`);
  }
  const candidate = structuredClone(stamped);
  const violations = validateProgramReceipt(candidate);
  if (violations.length > 0) {
    throw new Error(`program receipt mint rejected: ${violations.join(', ')}`);
  }
  return deepFreeze(candidate);
}

function deepFreeze<T>(value: T): T {
  if (value !== null && typeof value === 'object') {
    for (const child of Object.values(value)) deepFreeze(child);
    Object.freeze(value);
  }
  return value;
}
